import {
  propertyLabel,
  resolveItems,
  resolveProperty,
  type Schema,
} from "../jsonSchemaUtils";
import { deepEqual } from "./pendingFormat";

/**
 * Schema-aware structural diff of two config objects, for the Review & Save
 * dialog (human-readable change list) and the per-field pending markers on
 * cards.
 *
 * The walk is driven by the VALUES (old/new JSON); the schema only refines
 * labels and table-row identities, so a missing or partial schema degrades
 * to humanized keys instead of failing. Entries come out FLAT with the
 * nesting recorded in ``path`` and pre-joined into ``label`` ("Selection -
 * Patterns") - the dialog renders a scannable list, not a tree.
 */

type Json = Record<string, unknown>;

export type DiffKind = "changed" | "added" | "removed";

export interface ScalarDiffEntry {
  type: "scalar";
  kind: DiffKind;
  path: string[];
  label: string;
  oldValue?: unknown;
  newValue?: unknown;
}

export interface ListDiffEntry {
  type: "list";
  path: string[];
  label: string;
  added: unknown[];
  removed: unknown[];
  /** Same items, different order (nothing added or removed). */
  reordered: boolean;
}

export interface RowCellChange {
  key: string;
  label: string;
  oldValue?: unknown;
  newValue?: unknown;
}

export interface RowChange {
  kind: DiffKind;
  /** Display value of the row's identity column (or "Row N" fallback). */
  identity: string;
  /** changed: only the changed columns; added/removed: every column. */
  cells: RowCellChange[];
}

export interface TableDiffEntry {
  type: "table";
  path: string[];
  label: string;
  rows: RowChange[];
  /** Same rows, different order (no row-level changes). */
  reordered: boolean;
}

/**
 * Bail-out for shapes without a dedicated rendering (mixed/nested arrays,
 * type changes, whole added/removed subtrees) - rendered as JSON.
 */
export interface OpaqueDiffEntry {
  type: "opaque";
  kind: DiffKind;
  path: string[];
  label: string;
  oldValue?: unknown;
  newValue?: unknown;
}

export type ConfigDiffEntry =
  | ScalarDiffEntry
  | ListDiffEntry
  | TableDiffEntry
  | OpaqueDiffEntry;

function isPlainObject(v: unknown): v is Json {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isPrimitive(v: unknown): boolean {
  return !isPlainObject(v) && !Array.isArray(v);
}

const joinLabel = (chain: string[], label: string) =>
  [...chain, label].join(" - ");

/** Union of keys: schema property order first, then extras as they appear. */
function orderedKeys(oldObj: Json, newObj: Json, objSchema: Schema | null) {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => {
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  };
  Object.keys(objSchema?.properties ?? {}).forEach(push);
  Object.keys(oldObj).forEach(push);
  Object.keys(newObj).forEach(push);
  // Schema keys absent from both values would diff as no-ops; drop them.
  return out.filter((k) => k in oldObj || k in newObj);
}

/** Multiset diff of two primitive arrays (order-insensitive). */
function diffPrimitiveList(
  oldArr: unknown[],
  newArr: unknown[],
  path: string[],
  label: string,
): ListDiffEntry {
  const removedPool = [...oldArr];
  const added: unknown[] = [];
  for (const item of newArr) {
    const at = removedPool.findIndex((v) => deepEqual(v, item));
    if (at >= 0) removedPool.splice(at, 1);
    else added.push(item);
  }
  // Only called when the arrays differ, so an empty add/remove set can only
  // mean the same items in a different order.
  return {
    type: "list",
    path,
    label,
    added,
    removed: removedPool,
    reordered: added.length === 0 && removedPool.length === 0,
  };
}

/**
 * Identity column for matching table rows across the edit: first required
 * string property of the row schema, else its first string property, else
 * (schema-less) the first string-valued key of a sample row.
 */
function pickIdentityKey(
  itemsSchema: Schema | null,
  root: Schema | null,
  oldRows: Json[],
  newRows: Json[],
): string | null {
  if (itemsSchema?.properties) {
    const props: Record<string, Schema> = itemsSchema.properties;
    const isStringProp = (key: string) => {
      const { resolved } = resolveProperty(itemsSchema, root, key);
      return resolved?.type === "string";
    };
    const required: string[] = Array.isArray(itemsSchema.required)
      ? itemsSchema.required
      : [];
    for (const key of required) if (props[key] && isStringProp(key)) return key;
    for (const key of Object.keys(props)) if (isStringProp(key)) return key;
    return null;
  }
  const sample = oldRows[0] ?? newRows[0];
  if (sample) {
    for (const key of Object.keys(sample)) {
      if (typeof sample[key] === "string") return key;
    }
  }
  return null;
}

function rowIdentity(row: Json, idKey: string | null, index: number): string {
  const value = idKey ? row[idKey] : undefined;
  if (typeof value === "string" && value !== "") return value;
  return `Row ${index + 1}`;
}

/** All columns of a row as one-sided cells (for added/removed rows). */
function rowCells(
  row: Json,
  side: "old" | "new",
  itemsSchema: Schema | null,
): RowCellChange[] {
  return Object.keys(row).map((key) => ({
    key,
    label: propertyLabel(key, itemsSchema?.properties?.[key]),
    [side === "old" ? "oldValue" : "newValue"]: row[key],
  }));
}

/** Row-matched diff of two object arrays (``list[Model]`` tables). */
function diffObjectTable(
  oldRows: Json[],
  newRows: Json[],
  itemsSchema: Schema | null,
  root: Schema | null,
  path: string[],
  label: string,
): TableDiffEntry {
  const idKey = pickIdentityKey(itemsSchema, root, oldRows, newRows);

  // Pass 1: pair rows sharing the identity value (duplicates pair in order).
  const pairs: Array<{ o: number; n: number }> = [];
  const usedOld = new Set<number>();
  const usedNew = new Set<number>();
  if (idKey) {
    const newByIdentity = new Map<unknown, number[]>();
    newRows.forEach((row, i) => {
      const queue = newByIdentity.get(row[idKey]) ?? [];
      queue.push(i);
      newByIdentity.set(row[idKey], queue);
    });
    oldRows.forEach((row, i) => {
      const queue = newByIdentity.get(row[idKey]);
      if (queue && queue.length > 0) {
        const n = queue.shift() as number;
        pairs.push({ o: i, n });
        usedOld.add(i);
        usedNew.add(n);
      }
    });
  }

  // Pass 2: zip the leftovers in order - this is what surfaces an identity
  // RENAME as a changed cell instead of a remove+add pair.
  const leftoverOld = oldRows.map((_, i) => i).filter((i) => !usedOld.has(i));
  const leftoverNew = newRows.map((_, i) => i).filter((i) => !usedNew.has(i));
  const zipCount = Math.min(leftoverOld.length, leftoverNew.length);
  for (let k = 0; k < zipCount; k++) {
    pairs.push({ o: leftoverOld[k], n: leftoverNew[k] });
  }
  const removedIdx = leftoverOld.slice(zipCount);
  const addedIdx = leftoverNew.slice(zipCount);

  const rows: RowChange[] = [];
  const removedRows = [...removedIdx];
  const addedRows = [...addedIdx];
  pairs.sort((a, b) => a.o - b.o);
  for (const { o, n } of pairs) {
    const oldRow = oldRows[o];
    const newRow = newRows[n];
    const keys = orderedKeys(oldRow, newRow, itemsSchema);
    const cells: RowCellChange[] = [];
    for (const key of keys) {
      // Same effective-value rule as diffValuePair: absent = schema default.
      const { raw, resolved } = resolveProperty(itemsSchema, root, key);
      const dflt = resolved?.default;
      const inOld = key in oldRow || dflt !== undefined;
      const inNew = key in newRow || dflt !== undefined;
      const oldVal = key in oldRow ? oldRow[key] : dflt;
      const newVal = key in newRow ? newRow[key] : dflt;
      if (inOld && inNew && deepEqual(oldVal, newVal)) continue;
      cells.push({
        key,
        label: propertyLabel(key, raw),
        ...(inOld ? { oldValue: oldVal } : null),
        ...(inNew ? { newValue: newVal } : null),
      });
    }
    if (cells.length === 0) continue;
    // A zipped (identity-mismatched) pair only reads as the same row EDITED
    // while at most half of its columns changed; beyond that it is a genuine
    // replace and shows better as remove + add.
    if (!usedOld.has(o) && cells.length * 2 > keys.length) {
      removedRows.push(o);
      addedRows.push(n);
      continue;
    }
    rows.push({
      kind: "changed",
      identity: rowIdentity(newRow, idKey, n),
      cells,
    });
  }
  for (const i of removedRows.sort((a, b) => a - b)) {
    rows.push({
      kind: "removed",
      identity: rowIdentity(oldRows[i], idKey, i),
      cells: rowCells(oldRows[i], "old", itemsSchema),
    });
  }
  for (const i of addedRows.sort((a, b) => a - b)) {
    rows.push({
      kind: "added",
      identity: rowIdentity(newRows[i], idKey, i),
      cells: rowCells(newRows[i], "new", itemsSchema),
    });
  }

  // Arrays differ (caller checked) with no row-level changes -> same rows
  // in a different order.
  return { type: "table", path, label, rows, reordered: rows.length === 0 };
}

function diffValuePair(
  key: string,
  oldObj: Json,
  newObj: Json,
  objSchema: Schema | null,
  root: Schema | null,
  path: string[],
  chain: string[],
  out: ConfigDiffEntry[],
): void {
  const { raw, resolved } = resolveProperty(objSchema, root, key);
  // Compare EFFECTIVE values: an absent key means "the schema default" to
  // Pydantic and to the edit form, so the diff must read it the same way
  // (e.g. an unset boolean toggled on is "No -> Yes", and editing one field
  // of a defaulted nested model is a field change, not an "added" object).
  const dflt = resolved?.default;
  const inOld = key in oldObj || dflt !== undefined;
  const inNew = key in newObj || dflt !== undefined;
  const oldVal = key in oldObj ? oldObj[key] : dflt;
  const newVal = key in newObj ? newObj[key] : dflt;
  if (inOld && inNew && deepEqual(oldVal, newVal)) return;

  const label = propertyLabel(key, raw);
  const fullLabel = joinLabel(chain, label);
  const childPath = [...path, key];

  if (!inOld || !inNew) {
    const kind: DiffKind = inNew ? "added" : "removed";
    const value = inNew ? newVal : oldVal;
    out.push({
      type: isPrimitive(value) ? "scalar" : "opaque",
      kind,
      path: childPath,
      label: fullLabel,
      ...(inNew ? { newValue: value } : { oldValue: value }),
    });
    return;
  }

  if (isPlainObject(oldVal) && isPlainObject(newVal)) {
    walkObject(oldVal, newVal, resolved, root, childPath, [...chain, label], out);
    return;
  }

  if (Array.isArray(oldVal) && Array.isArray(newVal)) {
    const both = [...oldVal, ...newVal];
    if (both.every(isPrimitive)) {
      out.push(diffPrimitiveList(oldVal, newVal, childPath, fullLabel));
      return;
    }
    if (both.every(isPlainObject)) {
      out.push(
        diffObjectTable(
          oldVal as Json[],
          newVal as Json[],
          resolveItems(resolved, root),
          root,
          childPath,
          fullLabel,
        ),
      );
      return;
    }
    out.push({
      type: "opaque",
      kind: "changed",
      path: childPath,
      label: fullLabel,
      oldValue: oldVal,
      newValue: newVal,
    });
    return;
  }

  if (isPrimitive(oldVal) && isPrimitive(newVal)) {
    out.push({
      type: "scalar",
      kind: "changed",
      path: childPath,
      label: fullLabel,
      oldValue: oldVal,
      newValue: newVal,
    });
    return;
  }

  // Type changed (object <-> array <-> primitive).
  out.push({
    type: "opaque",
    kind: "changed",
    path: childPath,
    label: fullLabel,
    oldValue: oldVal,
    newValue: newVal,
  });
}

function walkObject(
  oldObj: Json,
  newObj: Json,
  objSchema: Schema | null,
  root: Schema | null,
  path: string[],
  chain: string[],
  out: ConfigDiffEntry[],
): void {
  for (const key of orderedKeys(oldObj, newObj, objSchema)) {
    diffValuePair(key, oldObj, newObj, objSchema, root, path, chain, out);
  }
}

/**
 * Diff two config objects into flat, renderable entries. ``schema`` is the
 * config's JSON Schema (or null - labels then fall back to humanized keys).
 */
export function diffConfigTree(
  oldCfg: unknown,
  newCfg: unknown,
  schema: Schema | null,
): ConfigDiffEntry[] {
  const out: ConfigDiffEntry[] = [];
  const oldObj = isPlainObject(oldCfg) ? oldCfg : oldCfg == null ? {} : null;
  const newObj = isPlainObject(newCfg) ? newCfg : newCfg == null ? {} : null;
  if (oldObj !== null && newObj !== null) {
    walkObject(oldObj, newObj, schema, schema, [], [], out);
  } else if (!deepEqual(oldCfg, newCfg)) {
    // A config that is not an object at all (bad data) - still show it.
    out.push({
      type: "opaque",
      kind: "changed",
      path: [],
      label: "Value",
      oldValue: oldCfg,
      newValue: newCfg,
    });
  }
  return out;
}

/**
 * Dotted paths of every changed field PLUS all ancestors ("selection",
 * "selection.patterns") - drives the per-field pending markers on cards.
 */
export function changedPathSet(entries: ConfigDiffEntry[]): Set<string> {
  const paths = new Set<string>();
  for (const entry of entries) {
    for (let depth = 1; depth <= entry.path.length; depth++) {
      paths.add(entry.path.slice(0, depth).join("."));
    }
  }
  return paths;
}
